import React, { createContext, useContext, useState, useEffect } from 'react';
import { getStoredUser } from '../utils/authStorage';
import { getUserById } from '../api/userService';
import { getSetting, preloadSettings } from '../services/settingsService';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [colorScheme, setColorScheme] = useState('default');
  const [disableSyntaxHighlighting, setDisableSyntaxHighlighting] = useState(false);

  useEffect(() => {
    loadUserPreferences();
    
    // Listen for storage changes (when user logs in/out or profile updates)
    const handleStorageChange = (e) => {
      if (e.key === 'webmonitor_auth_user_v1' || e.key === null) {
        loadUserPreferences();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom event when user data is updated in same tab
    const handleUserUpdate = () => {
      loadUserPreferences();
    };
    
    window.addEventListener('userUpdated', handleUserUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userUpdated', handleUserUpdate);
    };
  }, []);

  const loadUserPreferences = async () => {
    try {
      const currentUser = getStoredUser();

      // CRITICAL: Only fetch settings when user is logged in. /api/settings requires auth.
      // Calling preloadSettings() when unauthenticated causes 401 → redirect to /login → reload loop.
      let themeScheme = 'default'; // default = light theme (WordPress blue)
      if (currentUser?.id || currentUser?._id) {
        await preloadSettings();
        const globalTheme = await getSetting('uiTheme', 'light');
        if (globalTheme === 'dark') {
          themeScheme = 'modern';
        }
      }
      if (currentUser?.id || currentUser?._id) {
        // User-specific theme takes precedence over global settings
        // First check if preferences are in stored user (faster)
        if (currentUser?.adminColorScheme !== undefined) {
          const scheme = currentUser.adminColorScheme || themeScheme;
          setColorScheme(scheme);
          applyTheme(scheme);
          
          // Load syntax highlighting preference
          if (currentUser?.disableSyntaxHighlighting !== undefined) {
            setDisableSyntaxHighlighting(currentUser.disableSyntaxHighlighting);
            applySyntaxHighlighting(currentUser.disableSyntaxHighlighting);
          }
          return;
        }
        
        // Otherwise fetch from API
        const response = await getUserById(currentUser.id || currentUser._id);
        const userData = response.user;
        const scheme = userData?.adminColorScheme || themeScheme;
        setColorScheme(scheme);
        applyTheme(scheme);
        
        // Load syntax highlighting preference
        const disableHighlight = userData?.disableSyntaxHighlighting || false;
        setDisableSyntaxHighlighting(disableHighlight);
        applySyntaxHighlighting(disableHighlight);
      } else {
        // No user logged in, use global theme
        setColorScheme(themeScheme);
        applyTheme(themeScheme);
        setDisableSyntaxHighlighting(false);
        applySyntaxHighlighting(false);
      }
    } catch (error) {
      console.error('Failed to load user preferences:', error);
      applyTheme('default');
      setDisableSyntaxHighlighting(false);
      applySyntaxHighlighting(false);
    }
  };

  const loadUserTheme = loadUserPreferences; // Keep for backward compatibility

  const applyTheme = (scheme) => {
    // Remove all existing theme classes
    const root = document.documentElement;
    const existingClasses = Array.from(root.classList).filter(cls => cls.startsWith('theme-'));
    root.classList.remove(...existingClasses);
    
    // Add new theme class
    root.classList.add(`theme-${scheme}`);
    
    // Also apply to body for better coverage
    const body = document.body;
    const bodyClasses = Array.from(body.classList).filter(cls => cls.startsWith('theme-'));
    body.classList.remove(...bodyClasses);
    body.classList.add(`theme-${scheme}`);
  };

  const applySyntaxHighlighting = (disable) => {
    // Apply class to root element to control syntax highlighting globally
    const root = document.documentElement;
    if (disable) {
      root.classList.add('disable-syntax-highlighting');
    } else {
      root.classList.remove('disable-syntax-highlighting');
    }
  };

  const updateTheme = (newScheme) => {
    setColorScheme(newScheme);
    applyTheme(newScheme);
  };

  const updateSyntaxHighlighting = (disable) => {
    setDisableSyntaxHighlighting(disable);
    applySyntaxHighlighting(disable);
  };

  return (
    <ThemeContext.Provider value={{ 
      colorScheme, 
      updateTheme, 
      loadUserTheme,
      disableSyntaxHighlighting,
      updateSyntaxHighlighting
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

